import { type FC, useContext, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BackArrowIcon from '@assets/img/icons/arrow-back.svg?react';
import { AnimatePresence } from 'framer-motion';

import { useGetBingoEventQuery } from '@/api/queries/bingo';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import {
  H1,
  H2,
  StringSmall,
  TextMain,
} from '@/components/Typography/Typography';
import { Button } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import { ChooseOffer } from './ChooseOffer/ChooseOffer';
import { ConfirmBuy } from './ConfirmBuy/ConfirmBuy';
import { BuyBingoBallsContext } from './context';

interface BuyBingoBallsModalProps {
  source?: 'plus_button' | 'no_balance_modal';
}

const BuyBingoBallsModal: FC<BuyBingoBallsModalProps> = ({ source }) => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [activeOfferIndex, setActiveOfferIndex] = useState(0);
  const [isChooseOfferStep, setIsChooseOfferStep] = useState(true);
  const dispatch = useAppDispatch();
  const paymentStatus = usePaymentStatus();
  const { data: event } = useGetBingoEventQuery();
  const packages = event?.packages;

  const nextStep = (): void => {
    setIsChooseOfferStep(false);
  };

  const prevStep = (): void => {
    setIsChooseOfferStep(true);
  };

  useLayoutEffect(() => {
    dispatch(changePaymentStatus(undefined));
  }, []);

  return (
    <BuyBingoBallsContext.Provider
      value={{ activeOfferIndex, setActiveOfferIndex, source }}
    >
      <div className="relative w-full px-4 pt-5 pb-6 flex flex-col items-center">
        {paymentStatus === 'pending' ? (
          <PendingStatus />
        ) : paymentStatus === 'success' ? (
          <SuccessfullStatus>
            <div className="bg-white/10 rounded-[8px] relative p-[13px] opacity-[0.6] mt-8 flex items-center justify-center">
              <img
                src="/img/bingo/bingo-icon.png"
                alt=""
                className="w-28 h-28 object-contain"
              />
              <StringSmall
                weight="bold"
                className="bg-black/30 absolute bottom-[4px] left-1/2 -translate-x-1/2 !text-[24px] px-[8px] py-[4px] rounded-[8px]"
              >
                {packages?.[activeOfferIndex]?.ballCount}
              </StringSmall>
            </div>
          </SuccessfullStatus>
        ) : (
          <>
            <div className="w-full flex flex-col items-center relative z-[1]">
              <H1 weight="extrabold" className="mb-6">
                {t('Purchase')}
              </H1>
              <H2 weight="bold" className="mb-2">
                {t('Get more bingo balls!')}
              </H2>
              <StringSmall className="!text-white/80 text-center mb-5 max-w-[17.125rem]">
                {t('Open cells, complete lines, win big rewards!')}
              </StringSmall>
            </div>
            <AnimatePresence mode="sync">
              {isChooseOfferStep ? (
                <ChooseOffer nextStep={nextStep} />
              ) : (
                <ConfirmBuy />
              )}
            </AnimatePresence>
            {!isChooseOfferStep && (
              <Button
                onClick={prevStep}
                variant="transparent"
                className="!h-12 !w-[12.8125rem] flex items-center justify-center"
              >
                <div className="flex items-center gap-2">
                  <BackArrowIcon className="w-6 h-6 shrink-0" />
                  <TextMain weight="extrabold">{t('Back')}</TextMain>
                </div>
              </Button>
            )}
          </>
        )}
        {(paymentStatus === 'pending' || isChooseOfferStep) && (
          <Close
            onClick={() => closeModal()}
            imageVariant="modal"
            size="lg"
            className="w-16 h-16 shrink-0 mt-11"
          />
        )}
      </div>
    </BuyBingoBallsContext.Provider>
  );
};

export { BuyBingoBallsModal };
